// Foundation tab — the Foundation Fund income reconciliation (2026-09-18).
//
// Nine views, one per sheet of the hand-run Foundation-V2.xlsx: the register
// (members / not currently members), the bank credits matched to members, the
// member-by-month matrix, the GoCardless payments and their customer-by-month
// matrix, the lookups behind the matching, and the statements as supplied.
// Every view downloads as .xlsx; the header downloads the master workbook.
//
// Gated SG-Reports-Engagement OR Reports admin (nav, route and every API
// route). Data: /api/foundation/* (api/src/functions/foundation.ts); the
// matching is owned by app.RebuildFoundation (db/92).
import { useEffect, useMemo, useState } from 'react'
import writeXlsxFile from 'write-excel-file/browser'
import { RefreshBlock, MemberDialog, AssignDialog, ReferenceDialog, NewReferenceDialog, DonorDialog } from './foundationEdit'
import { StatementUpload } from './foundationUpload'
import './foundation.css'

// ---------------------------------------------------------------------------
//  Types (mirror api/src/functions/foundation.ts)
// ---------------------------------------------------------------------------
interface MetaRow { source: string; refreshed_at: string | null; row_count: number; status: string; message: string | null }
interface Summary {
  meta: MetaRow[]
  register: { member?: number; not_current?: number }
  bank: {
    statements: number; rows: number; duplicates: number; debits: number; first_date: string | null; last_date: string | null
    byYear: { year: number; bucket: string; n: number; total: number }[]
    coverage: { month: string; rows: number; credits: number | null; debits: number | null; first_date: string; last_date: string; unmatched: number }[]
  }
  gocardless: { customers: number; payments: number; not_collected: number; no_donor: number; byYearCode: { year: number; code: string; n: number; total: number }[] }
  donorfy: { list_members: number; in_register: number; members_not_tagged: number; not_in_register: number; byYear: { year: number; n: number; total: number }[] }
}
interface MemberRow {
  member_id: number; foundation_ref: string; foundation_no: number | null; first_name: string | null; surname: string | null
  donorfy_no: number | null; donorfy_external_key: string | null; email: string | null; status: 'member' | 'not_current'
  payment_type: string | null; comment: string | null; source: string
}
interface MemberYearRow { member_id: number; year: number; n: number; total: number }
interface DonorfyOnlyRow { constituent_number: number; first_name: string | null; last_name: string | null; email: string | null; external_key: string | null }
interface MembersPayload { members: MemberRow[]; bankYears: MemberYearRow[]; gcYears: MemberYearRow[]; donorfyTagged: number[]; donorfyOnly: DonorfyOnlyRow[] }
interface BankRow {
  bank_txn_id: number; statement_id: number; source_name: string; row_ordinal: number; txn_date: string; narrative: string; txn_type: string | null
  debit: number | null; credit: number | null; balance: number | null; in_reconciliation: boolean; reference_key: string | null
  member_id: number | null; match_method: string | null; foundation_ref: string | null; foundation_no: number | null
  first_name: string | null; surname: string | null; donorfy_no: number | null; member_status: string | null
}
interface BankMonthRow {
  member_id: number; foundation_ref: string | null; foundation_no: number | null; first_name: string | null; surname: string | null
  donorfy_no: number | null; status: string | null; month: number; n: number; total: number
}
interface GcPaymentRow {
  payment_id: string; customer_id: string | null; charge_date: string; amount_pence: number; status: string | null; is_collected: boolean; description: string | null; source: string
  given_name: string | null; family_name: string | null; email: string | null; address_line1: string | null; address_line2: string | null
  city: string | null; postal_code: string | null; donation_code: string | null; code_source: string | null; donorfy_no: number | null
  member_id: number | null; foundation_ref: string | null; member_status: string | null
}
interface GcMonthRow {
  customer_id: string | null; given_name: string | null; family_name: string | null; email: string | null; donorfy_no: number | null
  foundation_ref: string | null; donation_code: string | null; month: number; n: number; total: number
}
interface ReferenceRow {
  reference_key: string; member_id: number; foundation_ref: string; first_name: string | null; surname: string | null
  donorfy_no: number | null; status: string; source: string; uses: number
}
interface DonorRow {
  donor_id: number; full_name: string; email: string | null; gc_customer_id: string | null; donorfy_no: number | null; donation_code: string | null
  member_id: number | null; foundation_ref: string | null; member_status: string | null; source: string; payments: number
}
interface CodeRow { code: string; description: string; is_foundation: boolean; sort_order: number }
interface Lookups { references: ReferenceRow[]; donors: DonorRow[]; codes: CodeRow[] }
interface StatementRow {
  statement_id: number; source_name: string; source_kind: string; account_sort_code: string | null; account_number: string | null
  account_alias: string | null; period_from: string | null; period_to: string | null; row_count: number; credit_total: number; debit_total: number
  uploaded_by: string | null; uploaded_at: string | null; notes: string | null; duplicate_rows: number; unmatched_rows: number
}

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------
const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
const money = (v: number | null | undefined) => (v === null || v === undefined ? '' : gbp.format(Number(v)))
const int = (v: number | null | undefined) => (v === null || v === undefined ? '' : Number(v).toLocaleString('en-GB'))
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso
}
const fmtDateTime = (iso: string | null | undefined) => (iso ? `${fmtDate(iso)} ${iso.slice(11, 16)}` : '')
const name = (first: string | null | undefined, surname: string | null | undefined) => [first ?? '', surname ?? ''].join(' ').trim()
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const round2 = (n: number) => Math.round(n * 100) / 100

function useJson<T>(url: string | null) {
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: string | null }>({ data: null, loading: Boolean(url), error: null })
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!url) { setState({ data: null, loading: false, error: null }); return }
    let alive = true
    setState((s) => ({ ...s, loading: true, error: null }))
    fetch(url, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          const j = (await r.json().catch(() => ({}))) as { error?: string }
          throw new Error(j.error || `HTTP ${r.status}`)
        }
        return (await r.json()) as T
      })
      .then((d) => { if (alive) setState({ data: d, loading: false, error: null }) })
      .catch((e: unknown) => { if (alive) setState({ data: null, loading: false, error: String((e as Error)?.message ?? e) }) })
    return () => { alive = false }
  }, [url, tick])
  return { ...state, reload: () => setTick((t) => t + 1) }
}

type Plain = string | number | null
interface XCell { value?: string | number; type?: StringConstructor | NumberConstructor; format?: string; fontWeight?: 'bold'; align?: 'right' }

/** One-sheet .xlsx of the view as shown. Money columns keep two decimals. */
async function downloadXlsx(fileName: string, sheet: string, headers: string[], rows: Plain[][], moneyCols: Set<number> = new Set()) {
  const header: XCell[] = headers.map((h) => ({ value: h, fontWeight: 'bold' }))
  const body: XCell[][] = rows.map((r) => r.map((v, i) => {
    if (typeof v === 'number') {
      const cell: XCell = { value: v, type: Number, align: 'right' }
      if (moneyCols.has(i)) cell.format = '#,##0.00'
      return cell
    }
    return { value: v ?? '', type: String }
  }))
  const columns = headers.map((h, i) => ({ width: Math.max(10, Math.min(64, Math.max(h.length + 4, ...rows.slice(0, 200).map((r) => String(r[i] ?? '').length + 2)))) }))
  const blob = await writeXlsxFile([{ data: [header, ...body], sheet: sheet.slice(0, 31), columns, stickyRowsCount: 1 }] as never).toBlob()
  triggerDownload(blob, fileName)
}

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}

const stamp = () => new Date().toISOString().slice(0, 10)

function StatusBadge({ status, credit }: { status: string | null | undefined; credit?: number | null }) {
  if (status === 'member') return <span className="fnd-badge fnd-badge--member">Member</span>
  if (status === 'not_current') return <span className="fnd-badge fnd-badge--not-current">Not current</span>
  if (credit !== undefined && credit !== null) return <span className="fnd-badge fnd-badge--unmatched">Unmatched</span>
  return null
}

function DownloadButton({ label = 'Download this view (.xlsx)', onClick, disabled }: { label?: string; onClick: () => Promise<void> | void; disabled?: boolean }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  return (
    <span className="fnd-head-actions">
      <button type="button" className="btn-secondary" disabled={disabled || busy} onClick={async () => {
        setBusy(true); setErr(null)
        try { await onClick() } catch (e) { setErr(String((e as Error)?.message ?? e)) } finally { setBusy(false) }
      }}>{busy ? 'Building…' : label}</button>
      {err && <span className="fnd-error">{err}</span>}
    </span>
  )
}

// ---------------------------------------------------------------------------
//  Views
// ---------------------------------------------------------------------------
type View = 'overview' | 'members' | 'not-current' | 'bank' | 'bank-monthly' | 'gocardless' | 'gocardless-monthly' | 'lookups' | 'statements'
const VIEWS: { key: View; label: string; blurb: string }[] = [
  { key: 'overview', label: 'Overview', blurb: 'What is held, the reconciliation by calendar year, which months the statements cover, and where the data came from.' },
  { key: 'members', label: 'Members', blurb: 'Current Foundation members with their bank income per calendar year and their Foundation-coded GoCardless payments per year. The workbook\u2019s F Members sheet.' },
  { key: 'not-current', label: 'Not currently members', blurb: 'Former or unmatched Foundation numbers, with the payment type and comment recorded against them, and any income by year.' },
  { key: 'bank', label: 'Bank payments', blurb: 'Every credit to the Foundation Fund account, matched to a member by the reference in the narrative. Unmatched credits are highlighted: Assign one to a member and its reference is remembered for the next statement.' },
  { key: 'bank-monthly', label: 'Payments by member and month', blurb: 'Bank credits by member and month for one calendar year; the unmatched credits are the last line. The workbook’s PIVOT sheet.' },
  { key: 'gocardless', label: 'GoCardless payments', blurb: 'Every GoCardless payment with the customer, the donation code and the Donorfy number. Foundation (F) is selected by default; choose All for every appeal. Payments not collected (failed, cancelled, pending) are shown greyed and never counted.' },
  { key: 'gocardless-monthly', label: 'GoCardless by customer and month', blurb: 'GoCardless payments by customer and month for one calendar year, filtered by donation code. The workbook’s Pivot_Gocardless sheet.' },
  { key: 'lookups', label: 'Lookups', blurb: 'The tables behind the matching: bank narrative references to members, GoCardless customers to Donorfy numbers and codes, and the donation codes. Change or delete a reference, edit a donor mapping, or add a reference by hand.' },
  { key: 'statements', label: 'Statements', blurb: 'The bank statements held, as supplied. Upload the month\u2019s Santander export (.csv or .xlsx) here; a row that repeats an earlier statement is shown but not counted, so re-uploading a month is harmless. Select a statement to see its rows.' },
]

export function FoundationScreen({ me }: { me: { isEngagement: boolean; isAdmin: boolean } }) {
  const enabled = me.isEngagement || me.isAdmin
  const [view, setView] = useState<View>('overview')
  const summary = useJson<Summary>(enabled ? '/api/foundation/summary' : null)
  const lookups = useJson<Lookups>(enabled ? '/api/foundation/lookups' : null)
  const [reloadKey, setReloadKey] = useState(0)
  const codes = lookups.data?.codes ?? []

  if (!enabled) {
    return (
      <div className="budget-pnl">
        <h2>Foundation</h2>
        <p>This area is limited to members of <code>SG-Reports-Engagement</code> and Reports admins. Ask Chris Flynn for access.</p>
      </div>
    )
  }

  const def = VIEWS.find((v) => v.key === view) ?? VIEWS[0]
  const meta = summary.data?.meta ?? []

  const downloadMaster = async () => {
    const r = await fetch('/api/foundation/export', { credentials: 'include' })
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string }
      throw new Error(j.error || `HTTP ${r.status}`)
    }
    triggerDownload(await r.blob(), `NYMR_Foundation_${stamp().replace(/-/g, '')}.xlsx`)
  }

  return (
    <div className="fnd">
      <div className="fnd-head">
        <div>
          <p className="budget-sub">Foundation Fund income &mdash; the register, the bank standing orders matched to members, and the GoCardless payments. Visible to <code>SG-Reports-Engagement</code> and Reports admins.</p>
          {meta.length > 0 && (
            <p className="fnd-status">
              {meta.map((m) => (
                <span key={m.source}>
                  <strong>{metaLabel(m.source)}</strong>: {m.refreshed_at ? `${fmtDateTime(m.refreshed_at)} UTC` : 'never'}{m.status !== 'ok' ? ` — ${m.status}` : ''}{m.message ? ` — ${m.message}` : ''}. {' '}
                </span>
              ))}
              {!meta.some((m) => m.source === 'gocardless') && <span>Nightly refresh from GoCardless and Donorfy: not yet run.</span>}
            </p>
          )}
          {summary.error && <p className="fnd-error">{summary.error}</p>}
          <RefreshBlock canFull={me.isAdmin} onCompleted={() => { setReloadKey((k) => k + 1); summary.reload(); lookups.reload() }} />
        </div>
        <div className="fnd-head-actions">
          <DownloadButton label="Download master workbook (.xlsx)" onClick={downloadMaster} />
        </div>
      </div>

      <div className="fnd-pills" role="tablist" aria-label="Foundation view">
        {VIEWS.map((v) => (
          <button key={v.key} type="button" role="tab" aria-selected={v.key === view} className={'fnd-pill' + (v.key === view ? ' active' : '')} onClick={() => setView(v.key)} title={v.blurb}>
            {v.label}
          </button>
        ))}
      </div>
      <p className="fnd-blurb">{def.blurb}</p>

      {view === 'overview' && <OverviewView summary={summary.data} loading={summary.loading} error={summary.error} />}
      {view === 'members' && <RegisterView key={`members-${reloadKey}`} status="member" />}
      {view === 'not-current' && <RegisterView key={`not-current-${reloadKey}`} status="not_current" />}
      {view === 'bank' && <BankView key={`bank-${reloadKey}`} />}
      {view === 'bank-monthly' && <BankMonthlyView key={`bank-monthly-${reloadKey}`} />}
      {view === 'gocardless' && <GoCardlessView key={`gocardless-${reloadKey}`} codes={codes} />}
      {view === 'gocardless-monthly' && <GoCardlessMonthlyView key={`gocardless-monthly-${reloadKey}`} codes={codes} />}
      {view === 'lookups' && <LookupsView data={lookups.data} loading={lookups.loading} error={lookups.error} reload={lookups.reload} />}
      {view === 'statements' && <StatementsView key={`statements-${reloadKey}`} onChanged={() => { summary.reload(); lookups.reload() }} />}
    </div>
  )
}

function metaLabel(source: string): string {
  switch (source) {
    case 'workbook-seed': return 'Seeded from the workbook'
    case 'rebuild': return 'Matching rebuilt'
    case 'gocardless': return 'GoCardless refreshed'
    case 'donorfy': return 'Donorfy refreshed'
    default: return source
  }
}

// ---- Overview --------------------------------------------------------------
function OverviewView({ summary, loading, error }: { summary: Summary | null; loading: boolean; error: string | null }) {
  if (error) return <p className="fnd-error">{error}</p>
  if (loading || !summary) return <p className="fnd-note">Loading…</p>
  const s = summary
  const years = Array.from(new Set(s.bank.byYear.map((r) => r.year))).sort()
  const cell = (year: number, bucket: string) => s.bank.byYear.find((r) => r.year === year && r.bucket === bucket)
  const sum = (bucket: string) => s.bank.byYear.filter((r) => r.bucket === bucket).reduce((a, r) => a + Number(r.total), 0)
  const cnt = (bucket: string) => s.bank.byYear.filter((r) => r.bucket === bucket).reduce((a, r) => a + Number(r.n), 0)
  const allCredits = s.bank.byYear.reduce((a, r) => a + Number(r.total), 0)
  const gcTotal = s.gocardless.byYearCode.reduce((a, r) => a + Number(r.total), 0)
  const gcYears = Array.from(new Set(s.gocardless.byYearCode.map((r) => r.year))).sort()
  const gcCodes = Array.from(new Set(s.gocardless.byYearCode.map((r) => r.code))).sort((a, b) => (a === 'F' ? -1 : b === 'F' ? 1 : a.localeCompare(b)))
  const gcCell = (year: number, code: string) => s.gocardless.byYearCode.find((r) => r.year === year && r.code === code)

  // Coverage: every month between the first and last statement row.
  const coverage = new Map(s.bank.coverage.map((c) => [c.month, c]))
  const months: { key: string; state: 'ok' | 'gap' | 'partial'; title: string }[] = []
  if (s.bank.first_date && s.bank.last_date) {
    const cur = new Date(s.bank.first_date.slice(0, 7) + '-01T00:00:00Z')
    const end = new Date(s.bank.last_date.slice(0, 7) + '-01T00:00:00Z')
    const today = new Date().toISOString().slice(0, 10)
    while (cur <= end) {
      const key = cur.toISOString().slice(0, 7)
      const c = coverage.get(key)
      if (!c) months.push({ key, state: 'gap', title: 'No statement rows for this month' })
      else {
        const lastDay = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 0)).toISOString().slice(0, 10)
        const startsLate = Number(c.first_date.slice(8, 10)) > 4
        const endsEarly = c.last_date < lastDay && lastDay < today && Number(lastDay.slice(8, 10)) - Number(c.last_date.slice(8, 10)) > 4
        months.push({
          key,
          state: startsLate || endsEarly ? 'partial' : 'ok',
          title: `${c.rows} rows, ${fmtDate(c.first_date)} to ${fmtDate(c.last_date)}, credits ${money(c.credits)}${c.unmatched ? `, ${c.unmatched} unmatched` : ''}`,
        })
      }
      cur.setUTCMonth(cur.getUTCMonth() + 1)
    }
  }
  const gaps = months.filter((m) => m.state === 'gap').map((m) => monthLabel(m.key))
  const partials = months.filter((m) => m.state === 'partial').map((m) => `${monthLabel(m.key)} (${fmtDate(coverage.get(m.key)!.first_date)} to ${fmtDate(coverage.get(m.key)!.last_date)})`)

  return (
    <div className="fnd">
      <div className="fnd-kpis">
        <Kpi label="Members" value={int(s.register.member ?? 0)} sub="current Foundation members" />
        <Kpi label="Not currently members" value={int(s.register.not_current ?? 0)} sub="former or unmatched numbers" />
        <Kpi label="Bank credits held" value={money(allCredits)} sub={`${int(cnt('member') + cnt('not_current') + cnt('unmatched'))} credits, ${fmtDate(s.bank.first_date)} to ${fmtDate(s.bank.last_date)}`} />
        <Kpi label="Unmatched credits" value={money(sum('unmatched'))} sub={`${int(cnt('unmatched'))} credits with no member`} />
        <Kpi label="GoCardless collected" value={money(gcTotal)} sub={`${int(s.gocardless.payments)} payments from ${int(s.gocardless.customers)} customers${s.gocardless.not_collected ? `, ${int(s.gocardless.not_collected)} not collected` : ''}${s.gocardless.no_donor ? `, ${int(s.gocardless.no_donor)} with no donor mapping` : ''}`} />
        <Kpi label="Statements held" value={int(s.bank.statements)} sub={`${int(s.bank.duplicates)} duplicate rows flagged, ${int(s.bank.debits)} debits`} />
        <Kpi label="Donorfy Foundation list" value={s.donorfy.list_members ? int(s.donorfy.list_members) : '—'} sub={s.donorfy.list_members ? `${int(s.donorfy.in_register)} in the register, ${int(s.donorfy.not_in_register)} not; ${int(s.donorfy.members_not_tagged)} members not tagged` : 'not yet refreshed from Donorfy'} />
      </div>

      <div className="fnd-two">
        <div className="fnd-section">
          <h3>Bank credits by calendar year</h3>
          <div className="fnd-table-wrap">
            <table className="hc-table">
              <thead><tr><th>Year</th><th className="ta-right">Members</th><th className="ta-right">Not current</th><th className="ta-right">Unmatched</th><th className="ta-right">All credits</th><th className="ta-right" title="Foundation Membership product or Trust Income - Foundation Account fund in Donorfy, any payment method">Recorded in Donorfy</th></tr></thead>
              <tbody>
                {years.map((y) => {
                  const m = cell(y, 'member'), n = cell(y, 'not_current'), u = cell(y, 'unmatched')
                  const tot = Number(m?.total ?? 0) + Number(n?.total ?? 0) + Number(u?.total ?? 0)
                  return (
                    <tr key={y}>
                      <td>{y}</td>
                      <td className="ta-right">{money(Number(m?.total ?? 0))} <span className="fnd-note">({int(m?.n ?? 0)})</span></td>
                      <td className="ta-right">{money(Number(n?.total ?? 0))} <span className="fnd-note">({int(n?.n ?? 0)})</span></td>
                      <td className="ta-right">{money(Number(u?.total ?? 0))} <span className="fnd-note">({int(u?.n ?? 0)})</span></td>
                      <td className="ta-right"><strong>{money(tot)}</strong></td>
                      <td className="ta-right">{s.donorfy.byYear.some((d) => d.year === y) ? money(Number(s.donorfy.byYear.find((d) => d.year === y)!.total)) : ''}</td>
                    </tr>
                  )
                })}
                <tr className="fnd-row-total">
                  <td>Total</td><td className="ta-right">{money(sum('member'))}</td><td className="ta-right">{money(sum('not_current'))}</td><td className="ta-right">{money(sum('unmatched'))}</td><td className="ta-right">{money(allCredits)}</td>
                  <td className="ta-right">{money(s.donorfy.byYear.filter((d) => years.includes(d.year)).reduce((a, d) => a + Number(d.total), 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="fnd-note">Members plus not-current plus unmatched equals every credit held, so the workbook&rsquo;s check line is the unmatched column: it should be nil. The last column is what Donorfy itself records against the Foundation, by any payment method; it is the fraction of the bank income that has been keyed into the CRM.</p>
        </div>

        <div className="fnd-section">
          <h3>GoCardless by calendar year and donation code</h3>
          <div className="fnd-table-wrap">
            <table className="hc-table">
              <thead><tr><th>Year</th>{gcCodes.map((c) => <th key={c} className="ta-right">{c}</th>)}<th className="ta-right">Total</th></tr></thead>
              <tbody>
                {gcYears.map((y) => (
                  <tr key={y}>
                    <td>{y}</td>
                    {gcCodes.map((c) => <td key={c} className="ta-right">{money(Number(gcCell(y, c)?.total ?? 0))}</td>)}
                    <td className="ta-right"><strong>{money(s.gocardless.byYearCode.filter((r) => r.year === y).reduce((a, r) => a + Number(r.total), 0))}</strong></td>
                  </tr>
                ))}
                <tr className="fnd-row-total">
                  <td>Total</td>
                  {gcCodes.map((c) => <td key={c} className="ta-right">{money(s.gocardless.byYearCode.filter((r) => r.code === c).reduce((a, r) => a + Number(r.total), 0))}</td>)}
                  <td className="ta-right">{money(gcTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="fnd-note">F is Foundation; the other codes are the appeals the same GoCardless account collects for. Amounts are payments confirmed or paid out, before GoCardless fees; failed, cancelled and pending payments are listed on the GoCardless payments view but not counted.</p>
        </div>
      </div>

      <div className="fnd-section">
        <h3>Statement coverage</h3>
        <div className="fnd-coverage">
          {months.map((m) => <span key={m.key} className={'fnd-month' + (m.state === 'gap' ? ' fnd-month--gap' : m.state === 'partial' ? ' fnd-month--partial' : '')} title={m.title}>{monthLabel(m.key)}</span>)}
        </div>
        {gaps.length > 0 && <p><strong>No statement rows at all for:</strong> {gaps.join(', ')}. Income for these months is not in any total until a statement is loaded.</p>}
        {partials.length > 0 && <p><strong>Only part of the month held for:</strong> {partials.join('; ')}.</p>}
        {gaps.length === 0 && partials.length === 0 && <p className="fnd-note">Every month between the first and last statement row has rows.</p>}
      </div>
    </div>
  )
}

function monthLabel(key: string) { return `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}` }

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="kpi-card">
      <div className="kpi-card__label">{label}</div>
      <div className="kpi-card__value">{value}</div>
      {sub && <div className="kpi-card__sub">{sub}</div>}
    </div>
  )
}

// ---- Register (Members / Not currently members) ----------------------------
function RegisterView({ status }: { status: 'member' | 'not_current' }) {
  const all = useJson<MembersPayload>('/api/foundation/members')
  const [q, setQ] = useState('')
  const [dialog, setDialog] = useState<{ member: MemberRow | null } | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const model = useMemo(() => {
    if (!all.data) return null
    const years = Array.from(new Set(all.data.bankYears.map((r) => r.year))).sort()
    const bank = new Map<string, number>()
    for (const r of all.data.bankYears) bank.set(`${r.member_id}|${r.year}`, Number(r.total))
    const gcYears = Array.from(new Set(all.data.gcYears.map((r) => r.year))).sort()
    const gc = new Map<string, number>()
    for (const r of all.data.gcYears) gc.set(`${r.member_id}|${r.year}`, Number(r.total))
    const refCount = new Map<string, number>()
    const donorfyCount = new Map<number, number>()
    for (const m of all.data.members) {
      refCount.set(m.foundation_ref, (refCount.get(m.foundation_ref) ?? 0) + 1)
      if (m.donorfy_no !== null) donorfyCount.set(m.donorfy_no, (donorfyCount.get(m.donorfy_no) ?? 0) + 1)
    }
    const needle = q.trim().toLowerCase()
    const rows = all.data.members.filter((m) => m.status === status).filter((m) => !needle || [m.foundation_ref, m.first_name, m.surname, m.donorfy_no, m.email, m.comment, m.payment_type].some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(needle)))
    const tagged = new Set(all.data.donorfyTagged)
    const donorfyOnly = all.data.donorfyOnly.filter((d) => !needle || [d.first_name, d.last_name, d.email, d.constituent_number].some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(needle)))
    return { years, gcYears, bank, gc, refCount, donorfyCount, rows, tagged, donorfyOnly }
  }, [all.data, q, status])

  if (all.error) return <p className="fnd-error">{all.error}</p>
  if (all.loading || !model) return <p className="fnd-note">Loading…</p>
  const { years, gcYears, bank, gc, refCount, donorfyCount, rows, tagged, donorfyOnly } = model
  const tagLabel = (m: MemberRow) => (tagged.size === 0 ? '' : m.donorfy_no !== null && tagged.has(m.donorfy_no) ? 'Yes' : 'No')
  const yearTotal = (y: number) => rows.reduce((a, m) => a + (bank.get(`${m.member_id}|${y}`) ?? 0), 0)
  const gcYearTotal = (y: number) => rows.reduce((a, m) => a + (gc.get(`${m.member_id}|${y}`) ?? 0), 0)
  const flags = (m: MemberRow) => {
    const out: string[] = []
    if ((refCount.get(m.foundation_ref) ?? 0) > 1) out.push('Number shared')
    if (m.donorfy_no !== null && (donorfyCount.get(m.donorfy_no) ?? 0) > 1) out.push('Donorfy No. shared')
    if (m.donorfy_no === null) out.push('No Donorfy No.')
    return out
  }
  const headers = ['Foundation No.', 'First name', 'Surname', 'Donorfy No.', 'Donorfy tag', 'Email', 'Payment type', 'Comment', ...years.map((y) => `Bank ${y}`), ...gcYears.map((y) => `GoCardless F ${y}`), 'Flags']
  const download = () => downloadXlsx(
    `NYMR_Foundation_${status === 'member' ? 'Members' : 'Not_Currently_Members'}_${stamp()}.xlsx`,
    status === 'member' ? 'F Members' : 'Not Currently a Member',
    headers,
    rows.map((m) => [m.foundation_ref, m.first_name, m.surname, m.donorfy_no, tagLabel(m), m.email, m.payment_type, m.comment, ...years.map((y) => round2(bank.get(`${m.member_id}|${y}`) ?? 0)), ...gcYears.map((y) => round2(gc.get(`${m.member_id}|${y}`) ?? 0)), flags(m).join(', ') || null]),
    new Set([...years.map((_, i) => 8 + i), ...gcYears.map((_, i) => 8 + years.length + i)]),
  )

  return (
    <div className="fnd">
      <div className="fnd-toolbar">
        <label className="fnd-field"><span>Search</span><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, number, comment" /></label>
        <span className="fnd-count">{int(rows.length)} {status === 'member' ? 'members' : 'people'}</span>
        <span className="fnd-spacer" />
        <button type="button" className="btn-secondary" onClick={() => setDialog({ member: null })}>Add {status === 'member' ? 'a member' : 'a former member'}</button>
        <DownloadButton onClick={download} disabled={rows.length === 0} />
      </div>
      <div className="fnd-table-wrap">
        <table className="hc-table">
          <thead>
            <tr>
              <th className="sticky-left">Foundation No.</th><th>First name</th><th>Surname</th><th className="ta-right">Donorfy No.</th><th title="Tagged Foundation in Donorfy (its Foundation Membership list), refreshed nightly">Donorfy tag</th><th>Email</th><th>Payment type</th><th>Comment</th>
              {years.map((y) => <th key={y} className="ta-right">Bank {y}</th>)}{gcYears.map((y) => <th key={`gc${y}`} className="ta-right" title="GoCardless payments under the Foundation code (F) only">GoCardless F {y}</th>)}<th>Flags</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.member_id}>
                <td className="sticky-left">{m.foundation_ref}</td><td>{m.first_name}</td><td>{m.surname}</td><td className="ta-right">{m.donorfy_no ?? ''}</td>
                <td>{tagLabel(m) === 'Yes' ? <span className="fnd-badge fnd-badge--member">Yes</span> : tagLabel(m) === 'No' ? <span className="fnd-badge fnd-badge--warn">No</span> : ''}</td>
                <td>{m.email}</td><td>{m.payment_type}</td><td className="wrap">{m.comment}</td>
                {years.map((y) => <td key={y} className="ta-right">{bank.has(`${m.member_id}|${y}`) ? money(bank.get(`${m.member_id}|${y}`)) : ''}</td>)}
                {gcYears.map((y) => <td key={`gc${y}`} className="ta-right">{gc.has(`${m.member_id}|${y}`) ? money(gc.get(`${m.member_id}|${y}`)) : ''}</td>)}
                <td>{flags(m).map((f) => <span key={f} className={'fnd-badge ' + (f === 'No Donorfy No.' ? 'fnd-badge--not-current' : 'fnd-badge--warn')} style={{ marginRight: 4 }}>{f}</span>)}</td>
                <td><button type="button" className="fnd-link" onClick={() => setDialog({ member: m })}>Edit</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={headers.length + 1} className="fnd-empty">No rows.</td></tr>}
            {rows.length > 0 && (
              <tr className="fnd-row-total">
                <td className="sticky-left">Total</td><td /><td /><td /><td /><td /><td /><td />
                {years.map((y) => <td key={y} className="ta-right">{money(yearTotal(y))}</td>)}{gcYears.map((y) => <td key={`gc${y}`} className="ta-right">{money(gcYearTotal(y))}</td>)}<td /><td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {note && <p className="fnd-note">{note}</p>}
      {dialog && <MemberDialog member={dialog.member} defaultStatus={status} onClose={() => setDialog(null)} onSaved={() => { all.reload(); setNote('Saved. The matching has been rebuilt.') }} />}
      {status === 'member' && tagged.size > 0 && (
        <div className="fnd-section">
          <h3>Tagged Foundation in Donorfy but not in the register <span className="fnd-note">({int(donorfyOnly.length)})</span></h3>
          <p className="fnd-note">Constituents on Donorfy&rsquo;s Foundation Membership list whose number is not against any register row. Either add them to the register or remove the tag in Donorfy.</p>
          <div className="fnd-table-wrap">
            <table className="hc-table">
              <thead><tr><th className="ta-right">Donorfy No.</th><th>First name</th><th>Last name</th><th>Email</th><th>External key</th></tr></thead>
              <tbody>
                {donorfyOnly.map((d) => <tr key={d.constituent_number}><td className="ta-right">{d.constituent_number}</td><td>{d.first_name}</td><td>{d.last_name}</td><td>{d.email}</td><td>{d.external_key}</td></tr>)}
                {donorfyOnly.length === 0 && <tr><td colSpan={5} className="fnd-empty">Everyone tagged in Donorfy is in the register.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Bank payments ---------------------------------------------------------
function BankView() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [unmatched, setUnmatched] = useState(false)
  const [showDup, setShowDup] = useState(false)
  const [q, setQ] = useState('')
  const [qApplied, setQApplied] = useState('')
  useEffect(() => { const t = setTimeout(() => setQApplied(q), 350); return () => clearTimeout(t) }, [q])
  const url = useMemo(() => {
    const p = new URLSearchParams()
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    if (unmatched) p.set('unmatched', '1')
    if (showDup) p.set('all', '1')
    if (qApplied.trim()) p.set('q', qApplied.trim())
    const s = p.toString()
    return '/api/foundation/bank' + (s ? '?' + s : '')
  }, [from, to, unmatched, showDup, qApplied])
  const data = useJson<{ rows: BankRow[] }>(url)
  const [assign, setAssign] = useState<BankRow | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const rows = data.data?.rows ?? []
  const credits = rows.filter((r) => r.in_reconciliation && r.credit !== null).reduce((a, r) => a + Number(r.credit), 0)
  const headers = ['Date', 'Narrative', 'Type', 'Credit', 'Debit', 'Balance', 'Reference', 'Member', 'Foundation No.', 'Donorfy No.', 'Status', 'Match', 'Statement', 'In reconciliation']
  const download = () => downloadXlsx(`NYMR_Foundation_Bank_Payments_${stamp()}.xlsx`, 'Bank Payments', headers,
    rows.map((r) => [fmtDate(r.txn_date), r.narrative, r.txn_type, r.credit, r.debit, r.balance, r.reference_key, r.member_id ? name(r.first_name, r.surname) : null, r.foundation_ref, r.donorfy_no,
      r.member_status === 'member' ? 'Member' : r.member_status === 'not_current' ? 'Not current' : r.credit !== null ? 'Unmatched' : null, r.match_method, r.source_name, r.in_reconciliation ? 'Yes' : 'No']),
    new Set([3, 4, 5]))

  return (
    <div className="fnd">
      <div className="fnd-toolbar">
        <label className="fnd-field"><span>From</span><input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="fnd-field"><span>To</span><input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} /></label>
        <label className="fnd-field"><span>Search</span><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="narrative, name, number" /></label>
        <label className="fnd-check"><input type="checkbox" checked={unmatched} onChange={(e) => setUnmatched(e.target.checked)} /> Unmatched only</label>
        <label className="fnd-check"><input type="checkbox" checked={showDup} onChange={(e) => setShowDup(e.target.checked)} /> Show duplicate rows</label>
        <span className="fnd-count">{data.loading ? 'Loading…' : `${int(rows.length)} rows, credits ${money(credits)}`}</span>
        <span className="fnd-spacer" />
        <DownloadButton onClick={download} disabled={rows.length === 0} />
      </div>
      {data.error && <p className="fnd-error">{data.error}</p>}
      {note && <p className="fnd-note">{note}</p>}
      {assign && <AssignDialog row={assign} onClose={() => setAssign(null)} onDone={(n) => { setNote(n); data.reload() }} />}
      <div className="fnd-table-wrap">
        <table className="hc-table">
          <thead><tr><th>Date</th><th>Narrative</th><th>Type</th><th className="ta-right">Credit</th><th className="ta-right">Debit</th><th className="ta-right">Balance</th><th>Reference</th><th>Member</th><th>Foundation No.</th><th className="ta-right">Donorfy No.</th><th>Status</th><th>Statement</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.bank_txn_id} className={!r.in_reconciliation ? 'fnd-row-dup' : r.credit !== null && r.member_id === null ? 'fnd-row-unmatched' : undefined} title={!r.in_reconciliation ? 'Repeats a row an earlier statement already holds; not counted' : undefined}>
                <td>{fmtDate(r.txn_date)}</td><td className="wrap">{r.narrative}</td><td>{r.txn_type}</td>
                <td className="ta-right">{money(r.credit)}</td><td className="ta-right">{money(r.debit)}</td><td className="ta-right">{money(r.balance)}</td>
                <td>{r.reference_key}</td><td>{r.member_id ? name(r.first_name, r.surname) : ''}</td><td>{r.foundation_ref}</td><td className="ta-right">{r.donorfy_no ?? ''}</td>
                <td>{!r.in_reconciliation ? <span className="fnd-badge fnd-badge--dup">Duplicate</span> : <StatusBadge status={r.member_status} credit={r.credit} />}</td>
                <td>{r.source_name}</td>
                <td>{r.credit !== null && r.in_reconciliation && <button type="button" className="fnd-link" onClick={() => setAssign(r)}>{r.member_id ? 'Change' : 'Assign'}</button>}</td>
              </tr>
            ))}
            {!data.loading && rows.length === 0 && <tr><td colSpan={13} className="fnd-empty">No bank rows match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---- Month matrix (shared by the two monthly views) ------------------------
interface MatrixLine { key: string; label: Plain[]; months: number[]; cls?: string }
function MatrixTable({ labelHeaders, lines, firstSticky = true }: { labelHeaders: string[]; lines: MatrixLine[]; firstSticky?: boolean }) {
  const grand = new Array<number>(12).fill(0)
  for (const l of lines) l.months.forEach((v, i) => { grand[i] += v })
  return (
    <div className="fnd-table-wrap">
      <table className="hc-table">
        <thead>
          <tr>
            {labelHeaders.map((h, i) => <th key={h} className={i === 0 && firstSticky ? 'sticky-left' : undefined}>{h}</th>)}
            {MONTHS.map((m) => <th key={m} className="ta-right">{m}</th>)}
            <th className="ta-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.key} className={l.cls}>
              {l.label.map((v, i) => <td key={i} className={i === 0 && firstSticky ? 'sticky-left' : typeof v === 'number' ? 'ta-right' : undefined}>{v ?? ''}</td>)}
              {l.months.map((v, i) => <td key={i} className="ta-right">{v ? money(v) : ''}</td>)}
              <td className="ta-right"><strong>{money(l.months.reduce((a, b) => a + b, 0))}</strong></td>
            </tr>
          ))}
          {lines.length === 0 && <tr><td colSpan={labelHeaders.length + 13} className="fnd-empty">No payments in this year.</td></tr>}
          {lines.length > 0 && (
            <tr className="fnd-row-total">
              {labelHeaders.map((h, i) => <td key={h} className={i === 0 && firstSticky ? 'sticky-left' : undefined}>{i === 0 ? 'Total' : ''}</td>)}
              {grand.map((v, i) => <td key={i} className="ta-right">{money(v)}</td>)}
              <td className="ta-right">{money(grand.reduce((a, b) => a + b, 0))}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
function matrixRows(lines: MatrixLine[]): Plain[][] {
  const out: Plain[][] = lines.map((l) => [...l.label, ...l.months.map((v) => round2(v)), round2(l.months.reduce((a, b) => a + b, 0))])
  const grand = new Array<number>(12).fill(0)
  for (const l of lines) l.months.forEach((v, i) => { grand[i] += v })
  if (lines.length) out.push([...lines[0].label.map((_, i) => (i === 0 ? 'Total' : null)), ...grand.map(round2), round2(grand.reduce((a, b) => a + b, 0))])
  return out
}

function YearSelect({ year, years, onChange }: { year: number; years: number[]; onChange: (y: number) => void }) {
  const opts = years.includes(year) ? years : [year, ...years]
  return (
    <label className="fnd-field"><span>Calendar year</span>
      <select value={year} onChange={(e) => onChange(Number(e.target.value))}>{opts.map((y) => <option key={y} value={y}>{y}</option>)}</select>
    </label>
  )
}

// ---- Payments by member and month ------------------------------------------
function BankMonthlyView() {
  const [year, setYear] = useState<number | null>(null)
  const url = '/api/foundation/bank/monthly' + (year ? `?year=${year}` : '')
  const data = useJson<{ year: number; years: number[]; rows: BankMonthRow[] }>(url)
  const shownYear = data.data?.year ?? year ?? new Date().getFullYear()
  const lines = useMemo<MatrixLine[]>(() => {
    if (!data.data) return []
    const map = new Map<number, MatrixLine>()
    for (const r of data.data.rows) {
      let l = map.get(r.member_id)
      if (!l) {
        l = r.member_id === 0
          ? { key: 'unmatched', label: [null, 'Unmatched credits', null, 'Unmatched'], months: new Array<number>(12).fill(0), cls: 'fnd-row-unmatched' }
          : { key: String(r.member_id), label: [r.foundation_ref, name(r.first_name, r.surname), r.donorfy_no, r.status === 'member' ? 'Member' : 'Not current'], months: new Array<number>(12).fill(0) }
        map.set(r.member_id, l)
      }
      l.months[r.month - 1] += Number(r.total)
    }
    const arr = [...map.values()]
    const un = arr.find((l) => l.key === 'unmatched')
    return [...arr.filter((l) => l.key !== 'unmatched'), ...(un ? [un] : [])]
  }, [data.data])
  const headers = ['Foundation No.', 'Name', 'Donorfy No.', 'Status']
  const download = () => downloadXlsx(`NYMR_Foundation_Payments_by_Month_${shownYear}.xlsx`, `Payments ${shownYear}`, [...headers, ...MONTHS, 'Total'], matrixRows(lines), new Set(Array.from({ length: 13 }, (_, i) => 4 + i)))
  return (
    <div className="fnd">
      <div className="fnd-toolbar">
        <YearSelect year={shownYear} years={data.data?.years ?? []} onChange={setYear} />
        <span className="fnd-count">{data.loading ? 'Loading…' : `${int(lines.length)} lines`}</span>
        <span className="fnd-spacer" />
        <DownloadButton onClick={download} disabled={lines.length === 0} />
      </div>
      {data.error && <p className="fnd-error">{data.error}</p>}
      <MatrixTable labelHeaders={headers} lines={lines} />
    </div>
  )
}

// ---- GoCardless payments ---------------------------------------------------
function CodeSelect({ code, codes, onChange }: { code: string; codes: CodeRow[]; onChange: (c: string) => void }) {
  return (
    <label className="fnd-field"><span>Donation code</span>
      <select value={code} onChange={(e) => onChange(e.target.value)}>
        <option value="F">F — Foundation</option>
        <option value="all">All codes</option>
        {codes.filter((c) => c.code !== 'F').map((c) => <option key={c.code} value={c.code}>{c.code} — {c.description}</option>)}
        <option value="(none)">No code</option>
      </select>
    </label>
  )
}

function GoCardlessView({ codes }: { codes: CodeRow[] }) {
  const [code, setCode] = useState('F')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [q, setQ] = useState('')
  const [qApplied, setQApplied] = useState('')
  useEffect(() => { const t = setTimeout(() => setQApplied(q), 350); return () => clearTimeout(t) }, [q])
  const url = useMemo(() => {
    const p = new URLSearchParams()
    p.set('code', code)
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    if (qApplied.trim()) p.set('q', qApplied.trim())
    return '/api/foundation/gocardless?' + p.toString()
  }, [code, from, to, qApplied])
  const data = useJson<{ rows: GcPaymentRow[] }>(url)
  const rows = data.data?.rows ?? []
  const total = rows.filter((r) => r.is_collected).reduce((a, r) => a + Number(r.amount_pence) / 100, 0)
  const notCollected = rows.filter((r) => !r.is_collected).length
  const headers = ['Date', 'Amount', 'Name', 'Email', 'Postcode', 'Donation', 'Description', 'Donorfy No.', 'Foundation No.', 'Member status', 'GoCardless status', 'Collected', 'Source']
  const download = () => downloadXlsx(`NYMR_Foundation_GoCardless_${code}_${stamp()}.xlsx`, 'GoCardless', headers,
    rows.map((r) => [fmtDate(r.charge_date), Number(r.amount_pence) / 100, name(r.given_name, r.family_name), r.email, r.postal_code, r.donation_code, r.description, r.donorfy_no, r.foundation_ref,
      r.member_status === 'member' ? 'Member' : r.member_status === 'not_current' ? 'Not current' : null, r.status, r.is_collected ? 'Yes' : 'No', r.source]), new Set([1]))
  return (
    <div className="fnd">
      <div className="fnd-toolbar">
        <CodeSelect code={code} codes={codes} onChange={setCode} />
        <label className="fnd-field"><span>From</span><input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="fnd-field"><span>To</span><input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} /></label>
        <label className="fnd-field"><span>Search</span><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, email, number" /></label>
        <span className="fnd-count">{data.loading ? 'Loading…' : `${int(rows.length)} payments, ${money(total)} collected${notCollected ? ` (${int(notCollected)} not collected, not counted)` : ''}`}</span>
        <span className="fnd-spacer" />
        <DownloadButton onClick={download} disabled={rows.length === 0} />
      </div>
      {data.error && <p className="fnd-error">{data.error}</p>}
      <div className="fnd-table-wrap">
        <table className="hc-table">
          <thead><tr><th>Date</th><th className="ta-right">Amount</th><th>Name</th><th>Email</th><th>Postcode</th><th>Donation</th><th>Description</th><th className="ta-right">Donorfy No.</th><th>Foundation No.</th><th>Member</th><th>GoCardless status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.payment_id} className={!r.is_collected ? 'fnd-row-dup' : undefined} title={!r.is_collected ? 'Not collected (failed, cancelled, charged back or still pending) — not counted' : undefined}>
                <td>{fmtDate(r.charge_date)}</td><td className="ta-right">{money(Number(r.amount_pence) / 100)}</td><td>{name(r.given_name, r.family_name)}</td><td>{r.email}</td><td>{r.postal_code}</td>
                <td>{r.donation_code ?? <span className="fnd-badge fnd-badge--unmatched">none</span>}</td><td>{r.description}</td><td className="ta-right">{r.donorfy_no ?? ''}</td><td>{r.foundation_ref}</td>
                <td><StatusBadge status={r.member_status} /></td><td>{r.status ? <>{r.status}{!r.is_collected && <> <span className="fnd-badge fnd-badge--warn">not counted</span></>}</> : (r.source === 'workbook' ? 'from the workbook export' : '')}</td>
              </tr>
            ))}
            {!data.loading && rows.length === 0 && <tr><td colSpan={11} className="fnd-empty">No GoCardless payments match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---- GoCardless by customer and month --------------------------------------
function GoCardlessMonthlyView({ codes }: { codes: CodeRow[] }) {
  const [year, setYear] = useState<number | null>(null)
  const [code, setCode] = useState('F')
  const url = '/api/foundation/gocardless/monthly?code=' + encodeURIComponent(code) + (year ? `&year=${year}` : '')
  const data = useJson<{ year: number; years: number[]; rows: GcMonthRow[] }>(url)
  const shownYear = data.data?.year ?? year ?? new Date().getFullYear()
  const lines = useMemo<MatrixLine[]>(() => {
    if (!data.data) return []
    const map = new Map<string, MatrixLine>()
    for (const r of data.data.rows) {
      const key = `${r.customer_id}|${r.donation_code ?? ''}`
      let l = map.get(key)
      if (!l) {
        l = { key, label: [name(r.given_name, r.family_name), r.donation_code ?? '(none)', r.donorfy_no, r.foundation_ref], months: new Array<number>(12).fill(0) }
        map.set(key, l)
      }
      l.months[r.month - 1] += Number(r.total)
    }
    return [...map.values()].sort((a, b) => String(a.label[0]).localeCompare(String(b.label[0])))
  }, [data.data])
  const headers = ['Name', 'Donation', 'Donorfy No.', 'Foundation No.']
  const download = () => downloadXlsx(`NYMR_Foundation_GoCardless_by_Month_${code}_${shownYear}.xlsx`, `GoCardless ${shownYear}`, [...headers, ...MONTHS, 'Total'], matrixRows(lines), new Set(Array.from({ length: 13 }, (_, i) => 4 + i)))
  return (
    <div className="fnd">
      <div className="fnd-toolbar">
        <YearSelect year={shownYear} years={data.data?.years ?? []} onChange={setYear} />
        <CodeSelect code={code} codes={codes} onChange={setCode} />
        <span className="fnd-count">{data.loading ? 'Loading…' : `${int(lines.length)} customers`}</span>
        <span className="fnd-spacer" />
        <DownloadButton onClick={download} disabled={lines.length === 0} />
      </div>
      {data.error && <p className="fnd-error">{data.error}</p>}
      <MatrixTable labelHeaders={headers} lines={lines} />
    </div>
  )
}

// ---- Lookups -----------------------------------------------------------------
function LookupsView({ data, loading, error, reload }: { data: Lookups | null; loading: boolean; error: string | null; reload: () => void }) {
  const [q, setQ] = useState('')
  const [refDialog, setRefDialog] = useState<ReferenceRow | null>(null)
  const [newRef, setNewRef] = useState(false)
  const [donorDialog, setDonorDialog] = useState<DonorRow | null>(null)
  if (error) return <p className="fnd-error">{error}</p>
  if (loading || !data) return <p className="fnd-note">Loading…</p>
  const needle = q.trim().toLowerCase()
  const has = (vals: (string | number | null | undefined)[]) => !needle || vals.some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(needle))
  const refs = data.references.filter((r) => has([r.reference_key, r.foundation_ref, r.first_name, r.surname, r.donorfy_no]))
  const donors = data.donors.filter((d) => has([d.full_name, d.email, d.donorfy_no, d.donation_code, d.foundation_ref]))
  return (
    <div className="fnd">
      <div className="fnd-toolbar">
        <label className="fnd-field"><span>Search</span><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="reference, name, number" /></label>
        <span className="fnd-spacer" />
        <button type="button" className="btn-secondary" onClick={() => setNewRef(true)}>Add a bank reference</button>
      </div>
      {refDialog && <ReferenceDialog referenceKey={refDialog.reference_key} memberId={refDialog.member_id} uses={Number(refDialog.uses)} onClose={() => setRefDialog(null)} onDone={reload} />}
      {newRef && <NewReferenceDialog onClose={() => setNewRef(false)} onDone={reload} />}
      {donorDialog && data && <DonorDialog donor={donorDialog} codes={data.codes} onClose={() => setDonorDialog(null)} onDone={reload} />}
      <div className="fnd-section">
        <div className="fnd-toolbar">
          <h3 style={{ margin: 0 }}>Bank references &rarr; members <span className="fnd-note">({int(refs.length)})</span></h3>
          <span className="fnd-spacer" />
          <DownloadButton onClick={() => downloadXlsx(`NYMR_Foundation_Bank_References_${stamp()}.xlsx`, 'Bank References', ['Reference (characters 30-54)', 'Foundation No.', 'Name', 'Donorfy No.', 'Status', 'Source', 'Rows matched'],
            refs.map((r) => [r.reference_key, r.foundation_ref, name(r.first_name, r.surname), r.donorfy_no, r.status === 'member' ? 'Member' : 'Not current', r.source, Number(r.uses)]))} disabled={refs.length === 0} />
        </div>
        <p className="fnd-note">The rule the workbook used: characters 30 to 54 of the bank narrative, looked up here. A credit whose slice is not in this table is unmatched.</p>
        <div className="fnd-table-wrap">
          <table className="hc-table">
            <thead><tr><th>Reference (characters 30&ndash;54)</th><th>Foundation No.</th><th>Name</th><th className="ta-right">Donorfy No.</th><th>Status</th><th>Source</th><th className="ta-right">Rows matched</th><th></th></tr></thead>
            <tbody>
              {refs.map((r) => (
                <tr key={r.reference_key}><td>{r.reference_key}</td><td>{r.foundation_ref}</td><td>{name(r.first_name, r.surname)}</td><td className="ta-right">{r.donorfy_no ?? ''}</td><td><StatusBadge status={r.status} /></td><td>{r.source}</td><td className="ta-right">{int(r.uses)}</td><td><button type="button" className="fnd-link" onClick={() => setRefDialog(r)}>Change</button></td></tr>
              ))}
              {refs.length === 0 && <tr><td colSpan={8} className="fnd-empty">No references match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="fnd-section">
        <div className="fnd-toolbar">
          <h3 style={{ margin: 0 }}>GoCardless customers &rarr; Donorfy and donation code <span className="fnd-note">({int(donors.length)})</span></h3>
          <span className="fnd-spacer" />
          <DownloadButton onClick={() => downloadXlsx(`NYMR_Foundation_GoCardless_Lookup_${stamp()}.xlsx`, 'GoCardless Lookup', ['Full name', 'Email', 'GoCardless customer', 'Donorfy No.', 'Donation', 'Foundation No.', 'Member status', 'Source', 'Payments'],
            donors.map((d) => [d.full_name, d.email, d.gc_customer_id, d.donorfy_no, d.donation_code, d.foundation_ref, d.member_status === 'member' ? 'Member' : d.member_status === 'not_current' ? 'Not current' : null, d.source, Number(d.payments)]))} disabled={donors.length === 0} />
        </div>
        <div className="fnd-table-wrap">
          <table className="hc-table">
            <thead><tr><th>Full name</th><th>Email</th><th>GoCardless customer</th><th className="ta-right">Donorfy No.</th><th>Donation</th><th>Foundation No.</th><th>Member</th><th>Source</th><th className="ta-right">Payments</th><th></th></tr></thead>
            <tbody>
              {donors.map((d) => (
                <tr key={d.donor_id}><td>{d.full_name}</td><td>{d.email}</td><td>{d.gc_customer_id}</td><td className="ta-right">{d.donorfy_no ?? ''}</td><td>{d.donation_code ?? <span className="fnd-badge fnd-badge--unmatched">none</span>}</td><td>{d.foundation_ref}</td><td><StatusBadge status={d.member_status} /></td><td>{d.source}</td><td className="ta-right">{int(d.payments)}</td><td><button type="button" className="fnd-link" onClick={() => setDonorDialog(d)}>Edit</button></td></tr>
              ))}
              {donors.length === 0 && <tr><td colSpan={10} className="fnd-empty">No customers match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="fnd-section">
        <h3>Donation codes</h3>
        <div className="fnd-table-wrap">
          <table className="hc-table">
            <thead><tr><th>Code</th><th>Description</th><th>Foundation</th></tr></thead>
            <tbody>{data.codes.map((c) => <tr key={c.code}><td>{c.code}</td><td>{c.description}</td><td>{c.is_foundation ? 'Yes' : ''}</td></tr>)}</tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ---- Statements ----------------------------------------------------------------
function StatementsView({ onChanged }: { onChanged: () => void }) {
  const list = useJson<{ statements: StatementRow[] }>('/api/foundation/statements')
  const [selected, setSelected] = useState<number | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const deleteStatement = async (id: number) => {
    setDeleting(true)
    try {
      const r = await fetch(`/api/foundation/statements/${id}`, { method: 'DELETE', credentials: 'include' })
      const j = (await r.json().catch(() => ({}))) as { error?: string; source_name?: string; manual_matches_lost?: number }
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      setNote(`Deleted "${j.source_name}"${j.manual_matches_lost ? ` (${j.manual_matches_lost} hand-made matches went with it)` : ''}. Totals have been rebuilt.`)
      if (selected === id) setSelected(null)
      list.reload(); onChanged()
    } catch (e) { setNote(String((e as Error)?.message ?? e)) } finally { setDeleting(false); setConfirmDelete(null) }
  }
  const rows = useJson<{ rows: BankRow[] }>(selected ? `/api/foundation/statements/${selected}` : null)
  const statements = list.data?.statements ?? []
  const sel = statements.find((s) => s.statement_id === selected) ?? null
  const rowHeaders = ['Row', 'Date', 'Narrative', 'Type', 'Credit', 'Debit', 'Balance', 'Reference', 'Member', 'Foundation No.', 'Donorfy No.', 'Status', 'In reconciliation']
  return (
    <div className="fnd">
      {list.error && <p className="fnd-error">{list.error}</p>}
      <StatementUpload onUploaded={(msg) => { setNote(msg); list.reload(); onChanged() }} />
      {note && <p className="fnd-note">{note}</p>}
      <div className="fnd-toolbar">
        <span className="fnd-count">{list.loading ? 'Loading…' : `${int(statements.length)} statements`}</span>
        <span className="fnd-spacer" />
        <DownloadButton label="Download the list (.xlsx)" onClick={() => downloadXlsx(`NYMR_Foundation_Statements_${stamp()}.xlsx`, 'Statements', ['Statement', 'Kind', 'From', 'To', 'Rows', 'Credits', 'Debits', 'Duplicate rows', 'Unmatched credits', 'Loaded by', 'Loaded at', 'Notes'],
          statements.map((s) => [s.source_name, s.source_kind, fmtDate(s.period_from), fmtDate(s.period_to), Number(s.row_count), Number(s.credit_total), Number(s.debit_total), Number(s.duplicate_rows), Number(s.unmatched_rows), s.uploaded_by, fmtDateTime(s.uploaded_at), s.notes]), new Set([5, 6]))} disabled={statements.length === 0} />
      </div>
      <div className="fnd-table-wrap">
        <table className="hc-table">
          <thead><tr><th>Statement</th><th>Kind</th><th>Account</th><th>From</th><th>To</th><th className="ta-right">Rows</th><th className="ta-right">Credits</th><th className="ta-right">Debits</th><th className="ta-right">Duplicates</th><th className="ta-right">Unmatched</th><th>Loaded</th><th>Notes</th><th></th></tr></thead>
          <tbody>
            {statements.map((s) => (
              <tr key={s.statement_id} className={'fnd-row-link' + (s.statement_id === selected ? ' fnd-row-total' : '')} onClick={() => setSelected(s.statement_id)}>
                <td><button type="button" className="fnd-link" onClick={(e) => { e.stopPropagation(); setSelected(s.statement_id) }}>{s.source_name}</button></td><td>{s.source_kind}</td>
                <td>{[s.account_sort_code, s.account_number].filter(Boolean).join(' ')}{s.account_alias ? ` ${s.account_alias}` : ''}</td>
                <td>{fmtDate(s.period_from)}</td><td>{fmtDate(s.period_to)}</td><td className="ta-right">{int(s.row_count)}</td><td className="ta-right">{money(s.credit_total)}</td><td className="ta-right">{money(s.debit_total)}</td>
                <td className="ta-right">{int(s.duplicate_rows)}</td><td className="ta-right">{int(s.unmatched_rows)}</td><td>{s.uploaded_by}{s.uploaded_at ? ` ${fmtDateTime(s.uploaded_at)}` : ''}</td><td className="wrap">{s.notes}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  {s.source_kind === 'upload' && (
                    <span className="fnd-head-actions">
                      <a className="fnd-link" href={`/api/foundation/statements/${s.statement_id}/file`} title="The file as uploaded">Original</a>
                      {confirmDelete === s.statement_id
                        ? <span><span className="fnd-note">Delete this upload and its rows? </span><button type="button" className="btn-link btn-link--danger" disabled={deleting} onClick={() => deleteStatement(s.statement_id)}>Yes, delete</button> <button type="button" className="btn-link" onClick={() => setConfirmDelete(null)}>No</button></span>
                        : <button type="button" className="btn-link btn-link--danger" onClick={() => setConfirmDelete(s.statement_id)}>Delete</button>}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {!list.loading && statements.length === 0 && <tr><td colSpan={13} className="fnd-empty">No statements held yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {sel && (
        <div className="fnd-section">
          <div className="fnd-toolbar">
            <h3 style={{ margin: 0 }}>{sel.source_name} <span className="fnd-note">{fmtDate(sel.period_from)} to {fmtDate(sel.period_to)}, {int(sel.row_count)} rows as supplied</span></h3>
            <span className="fnd-spacer" />
            <DownloadButton onClick={() => downloadXlsx(`NYMR_Foundation_Statement_${sel.source_name.replace(/[^A-Za-z0-9_-]+/g, '_')}.xlsx`, sel.source_name, rowHeaders,
              (rows.data?.rows ?? []).map((r) => [r.row_ordinal, fmtDate(r.txn_date), r.narrative, r.txn_type, r.credit, r.debit, r.balance, r.reference_key, r.member_id ? name(r.first_name, r.surname) : null, r.foundation_ref, r.donorfy_no,
                r.member_status === 'member' ? 'Member' : r.member_status === 'not_current' ? 'Not current' : r.credit !== null ? 'Unmatched' : null, r.in_reconciliation ? 'Yes' : 'No']), new Set([4, 5, 6]))} disabled={!rows.data?.rows.length} />
          </div>
          {rows.error && <p className="fnd-error">{rows.error}</p>}
          <div className="fnd-table-wrap">
            <table className="hc-table">
              <thead><tr><th className="ta-right">Row</th><th>Date</th><th>Narrative</th><th>Type</th><th className="ta-right">Credit</th><th className="ta-right">Debit</th><th className="ta-right">Balance</th><th>Reference</th><th>Member</th><th>Foundation No.</th><th className="ta-right">Donorfy No.</th><th>Status</th></tr></thead>
              <tbody>
                {(rows.data?.rows ?? []).map((r) => (
                  <tr key={r.bank_txn_id} className={!r.in_reconciliation ? 'fnd-row-dup' : r.credit !== null && r.member_id === null ? 'fnd-row-unmatched' : undefined}>
                    <td className="ta-right">{r.row_ordinal}</td><td>{fmtDate(r.txn_date)}</td><td className="wrap">{r.narrative}</td><td>{r.txn_type}</td>
                    <td className="ta-right">{money(r.credit)}</td><td className="ta-right">{money(r.debit)}</td><td className="ta-right">{money(r.balance)}</td><td>{r.reference_key}</td>
                    <td>{r.member_id ? name(r.first_name, r.surname) : ''}</td><td>{r.foundation_ref}</td><td className="ta-right">{r.donorfy_no ?? ''}</td>
                    <td>{!r.in_reconciliation ? <span className="fnd-badge fnd-badge--dup">Duplicate</span> : <StatusBadge status={r.member_status} credit={r.credit} />}</td>
                  </tr>
                ))}
                {rows.loading && <tr><td colSpan={12} className="fnd-empty">Loading…</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
